import React, { useEffect, useRef } from 'react';
import {
  DefaultTheme,
  NavigationContainer,
  createNavigationContainerRef,
  getStateFromPath,
  type NavigationState,
  type NavigatorScreenParams,
} from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { NO_GESTURE, PLEINE_LARGEUR, STACK_SCREEN_OPTIONS } from './screenOptions';
import { OnboardingNavigator, type OnboardingStackParamList } from './OnboardingNavigator';
import { TabNavigator, type TabParamList } from './TabNavigator';
import { ProfileNudgeModal } from '../components/ProfileNudgeModal';
import { goToProfilePersonalInfo } from './goToPersonalInfo';
import { fermerConfirmation } from '../utils/confirmation';
import { CoachingNavigator, type CoachingStackParamList } from './CoachingNavigator';
import { CadreBarreLaterale } from './CadreBarreLaterale';
import { ProfileCompletionScreen } from '../screens/ProfileCompletionScreen';
import { AppLoadingScreen } from '../screens/AppLoadingScreen';
import { NewPasswordScreen } from '../screens/NewPasswordScreen';
import { TermsGateNavigator, type TermsGateParamList } from './TermsGateNavigator';
import { useUserStore } from '../store/userStore';
import { useBirthDateGate } from '../hooks/useBirthDateGate';
import { useTermsGate } from '../hooks/useTermsGate';
import { useSessionPing } from '../hooks/useSessionPing';
import { useCollecteApp } from '../hooks/useCollecteApp';
import { LIENS_WEB } from './liensWeb';
import { prendreDestination } from './destinationWeb';
import { PaiementRetourScreen } from '../screens/PaiementRetourScreen';
import { GRAND_ECRAN, SUR_LE_WEB, colors } from '../theme/tokens';

export type RootStackParamList = {
  Onboarding: NavigatorScreenParams<OnboardingStackParamList> | undefined;
  AcceptTerms: NavigatorScreenParams<TermsGateParamList> | undefined;
  ProfileCompletion: undefined;
  Splash: undefined;
  // Écran verrou après un lien de réinitialisation : aucun paramètre, aucune adresse utile.
  NouveauMotDePasse: undefined;
  Main: NavigatorScreenParams<TabParamList> | undefined;
  Coaching: NavigatorScreenParams<CoachingStackParamList> | undefined;
  // `undefined` — AUCUN paramètre, et c'est une condition de fonctionnement : l'adresse est
  // composée par Stripe et par le moyen de paiement, jamais par nous.
  PaiementRetour: undefined;
};

const Stack = createNativeStackNavigator<RootStackParamList>();

// Le Coaching vit au-dessus des onglets : sur grand écran on lui rend la barre latérale.
function CoachingAvecBarre() {
  return (
    <CadreBarreLaterale>
      <CoachingNavigator />
    </CadreBarreLaterale>
  );
}

/**
 * FOND DE PAGE DES MARGES — un seul gris, sur grand écran seulement.
 *
 * Au-delà de la borne, le contenu d'un écran occupe 1200 px et le reste de la fenêtre laisse voir
 * ce que react-navigation peint derrière : son `DefaultTheme.colors.background`, un `#F2F2F2` qui
 * n'appartient pas à la charte. Les onglets branchés sur un écran (News, Offres) montraient de leur
 * côté leur propre `grey50` : deux gris voisins mais différents selon l'onglet, et une couture
 * visible entre eux.
 *
 * GARDÉ PAR `GRAND_ECRAN`, comme `controleLarge` et `colonneFormulaire` — et non posé sur le thème
 * global. Sur mobile ce fond n'est probablement jamais visible, un écran occupant toute la
 * fenêtre ; mais « probablement » n'est pas une garantie, et on ne déplace pas le mobile pour une
 * correction dont il n'a pas besoin. En dessous de la borne, `DefaultTheme` est rendu tel quel.
 */
const THEME_MARGES_UNIFIEES = {
  ...DefaultTheme,
  colors: { ...DefaultTheme.colors, background: colors.grey50 },
};

// Navigation impérative pour amener l'utilisateur sur l'écran d'auth depuis un effet (deep link).
export const navigationRef = createNavigationContainerRef<RootStackParamList>();

export function RootNavigator() {
  const session = useUserStore((state) => state.session);
  const recoveryPending = useUserStore((state) => state.recoveryPending);
  // Changer de langue re-monte cet arbre (clé posée par App) : sans rien d'autre, la navigation
  // repartirait de sa route initiale et projetterait la personne sur l'Accueil, alors qu'elle
  // vient de toucher un réglage dans l'onglet Profil. On mémorise l'état à chaque changement et on
  // le rejoue au montage suivant : elle reste exactement où elle était, en anglais.
  const savedNavState = useRef<NavigationState | undefined>(undefined);
  const authNoticeKey = useUserStore((state) => state.authNoticeKey);
  // Tracking d'ouverture pour les KPI de rétention (best-effort, 1/jour) — uniquement connecté.
  useSessionPing(!!session);
  useCollecteApp(!!session);

  // Un deep link de confirmation en échec (session non établie) amène l'utilisateur sur l'écran
  // d'auth, où AuthScreen affiche la notice — sinon il resterait sur l'onboarding sans explication.
  useEffect(() => {
    if (authNoticeKey && !session && navigationRef.isReady()) {
      navigationRef.navigate('Onboarding', { screen: 'Auth', initial: false });
    }
  }, [authNoticeKey, session]);
  // Étape 1 (obligatoire, légal) : acceptation des CGU si non encore consentie (surtout OAuth).
  const termsGate = useTermsGate(!!session);
  // Étape 2 (skippable) : date de naissance si manquante (email + OAuth).
  const gate = useBirthDateGate(!!session);

  // Pop-up « complète ton profil » : APRÈS les gates obligatoires uniquement (jamais avant).
  const promptActive = !!session && termsGate.status === 'done' && gate.status === 'done';

  /**
   * RETOUR À LA DESTINATION DEMANDÉE, une fois connecté (web).
   *
   * LA CONDITION EST `promptActive`, ET PAS SEULEMENT `session` — c'est le point délicat. Entre
   * l'arrivée de la session et le montage des écrans protégés, `renderContent` rend `Splash`, puis
   * éventuellement `AcceptTerms` ou `ProfileCompletion`. Rejouer l'adresse à ce moment-là viserait
   * des écrans qui ne sont pas montés : la navigation échouerait en silence, et l'utilisateur
   * atterrirait sur l'écran par défaut en croyant que son lien n'a jamais marché. On attend donc
   * que les deux portes obligatoires soient franchies — exactement la condition qui autorise déjà
   * la pop-up de profil.
   *
   * La destination est consommée en une fois : une navigation ultérieure ne la rejoue pas.
   */
  useEffect(() => {
    if (!SUR_LE_WEB || !promptActive || !navigationRef.isReady()) return;
    const chemin = prendreDestination();
    if (!chemin) return;
    const etat = getStateFromPath(chemin, LIENS_WEB.config);
    if (etat) navigationRef.reset(etat);
  }, [promptActive]);
  // `initial: false` empile Profile sous PersonalInfo → le retour reste dans l'onglet Profil.
  const goToPersonalInfo = () => goToProfilePersonalInfo(navigationRef);

  const renderContent = () => {
    if (!session) return <Stack.Screen name="Onboarding" component={OnboardingNavigator} options={SUR_LE_WEB ? PLEINE_LARGEUR : undefined} />;
    // Avant TOUTES les portes : la session de récupération ouvre déjà l'application, mais on y
    // entre après avoir choisi un nouveau mot de passe (ou en annulant, ce qui déconnecte).
    if (recoveryPending) {
      return <Stack.Screen name="NouveauMotDePasse" component={NewPasswordScreen} options={SUR_LE_WEB ? { ...NO_GESTURE, ...PLEINE_LARGEUR } : NO_GESTURE} />;
    }
    if (termsGate.status === 'loading' || gate.status === 'loading') {
      return <Stack.Screen name="Splash" component={AppLoadingScreen} />;
    }
    // CGU d'abord (bloquant), avant la date de naissance.
    if (termsGate.status === 'needed') {
      return (
        <Stack.Screen name="AcceptTerms" options={NO_GESTURE}>
          {() => <TermsGateNavigator onAccepted={termsGate.dismiss} outdated={termsGate.reason === 'outdated'} />}
        </Stack.Screen>
      );
    }
    if (gate.status === 'needed') {
      return (
        <Stack.Screen name="ProfileCompletion" options={NO_GESTURE}>
          {() => <ProfileCompletionScreen onComplete={gate.dismiss} onSkip={gate.dismiss} />}
        </Stack.Screen>
      );
    }
    return (
      <>
        <Stack.Screen name="Main" component={TabNavigator} options={PLEINE_LARGEUR} />
        {/* Coaching au niveau root → joignable globalement (FAB + bandeau accueil), au-dessus des onglets. */}
        <Stack.Screen name="Coaching" component={CoachingAvecBarre} options={SUR_LE_WEB ? PLEINE_LARGEUR : undefined} />
      </>
    );
  };

  return (
    <>
      <NavigationContainer
        ref={navigationRef}
        theme={GRAND_ECRAN ? THEME_MARGES_UNIFIEES : DefaultTheme}
        // WEB UNIQUEMENT. Sur mobile la prop reste `undefined`, donc le chemin des deep links
        // natifs — et avec lui le retour d'authentification tenu par `useAuthDeepLink` — n'est
        // pas touché du tout. Le pourquoi est détaillé en tête de `liensWeb`.
        linking={SUR_LE_WEB ? LIENS_WEB : undefined}
        initialState={savedNavState.current}
        onStateChange={(state) => {
          savedNavState.current = state;
          // LA MODALE DE CONFIRMATION NE S'APPARTIENT PAS. Sur le web, elle est un élément DOM posé
          // sur `body` : elle n'est dans aucun écran, donc rien ne la démonte quand l'écran qui l'a
          // ouverte disparaît. Mesuré le 18/09/2026 — la boîte « Supprimer ton compte ? » restait
          // peinte par-dessus l'écran suivant, gouvernée par plus personne. Même famille que les
          // scènes d'onglets inactives qui continuaient de peindre.
          //
          // Le routeur est le seul endroit qui SAIT que l'écran change, donc c'est lui qui referme.
          // Sur mobile, `fermerConfirmation` est un no-op : la prop est partagée, le comportement
          // natif ne bouge pas d'un pixel (voir `utils/confirmation.tsx`).
          fermerConfirmation();
        }}
      >
        <Stack.Navigator screenOptions={STACK_SCREEN_OPTIONS}>
          {renderContent()}
          {/*
            MONTÉ EN PERMANENCE, ET APRÈS `renderContent()` — les deux comptent.
            EN PERMANENCE : on revient de chez Klarna ou d'une authentification bancaire sans
            garantie que la session ait survécu au trajet. Placé dans une branche conditionnelle,
            l'écran n'existerait pas au retour et react-navigation retomberait sur l'onboarding —
            le résultat du paiement serait perdu derrière un écran de connexion, au moment précis
            où la personne veut savoir si elle a payé.
            APRÈS : react-navigation prend le PREMIER écran déclaré comme route de départ. Placé
            en tête, cette page de retour deviendrait l'écran d'accueil de l'application.
          */}
          <Stack.Screen name="PaiementRetour" options={PLEINE_LARGEUR}>
            {({ navigation }) => (
              <PaiementRetourScreen onRetourCoaching={() => navigation.navigate('Coaching')} />
            )}
          </Stack.Screen>
        </Stack.Navigator>
      </NavigationContainer>
      <ProfileNudgeModal active={promptActive} onComplete={goToPersonalInfo} />
    </>
  );
}
